import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Lock,
  Users,
  Globe,
  Pin,
  Tag,
  Link2,
  Palette,
  X,
  Plus,
} from 'lucide-react';
import { cn } from '../lib/utils';
import api from '../lib/api';

const COLORS = [
  { name: 'None', value: null },
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
];

const RESOURCE_TYPES = [
  { label: 'None', value: 'none' },
  { label: 'Project', value: 'project' },
  { label: 'User', value: 'user' },
  { label: 'Event', value: 'event' },
  { label: 'GitHub PR', value: 'github_pr' },
  { label: 'GitHub Issue', value: 'github_issue' },
  { label: 'Linear Issue', value: 'linear_issue' },
  { label: 'Slack Message', value: 'slack_message' },
];

export default function NoteEditor({
  note = null,
  onSave,
  onCancel,
  defaultLinkedResource = null, // { type, id, title }
}) {
  const [title, setTitle] = useState(note?.title || '');
  const [content, setContent] = useState(note?.content || '');
  const [visibility, setVisibility] = useState(note?.visibility || 'private');
  const [pinned, setPinned] = useState(note?.pinned || false);
  const [color, setColor] = useState(note?.color || null);
  const [tags, setTags] = useState(note?.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [linkedResourceType, setLinkedResourceType] = useState(
    note?.linked_resource_type || defaultLinkedResource?.type || 'none'
  );
  const [linkedResourceId, setLinkedResourceId] = useState(
    note?.linked_resource_id || defaultLinkedResource?.id || ''
  );
  const [linkedResourceTitle, setLinkedResourceTitle] = useState(
    note?.linked_resource_title || defaultLinkedResource?.title || ''
  );
  const [saving, setSaving] = useState(false);

  // Resource selection state
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Load projects when resource type is 'project'
  useEffect(() => {
    if (linkedResourceType === 'project') {
      loadProjects();
    }
  }, [linkedResourceType]);

  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
    }
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSave = async () => {
    if (!content.trim()) {
      return;
    }

    setSaving(true);
    try {
      const resourceType = linkedResourceType === 'none' ? null : linkedResourceType;
      await onSave({
        title: title.trim() || null,
        content: content.trim(),
        visibility,
        pinned,
        color,
        tags,
        linked_resource_type: resourceType,
        linked_resource_id: resourceType ? linkedResourceId || null : null,
        linked_resource_title: resourceType ? linkedResourceTitle || null : null,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleProjectSelect = (projectId) => {
    setLinkedResourceId(projectId);
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setLinkedResourceTitle(project.name);
    }
  };

  return (
    <div className="space-y-4">
      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="title">Title (optional)</Label>
        <Input
          id="title"
          placeholder="Give your note a title..."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          data-testid="note-title"
        />
      </div>

      {/* Content */}
      <div className="space-y-2">
        <Label htmlFor="content">Content</Label>
        <Textarea
          id="content"
          placeholder="Write your note here..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={6}
          className="resize-none"
          data-testid="note-content"
        />
      </div>

      {/* Visibility & Pin */}
      <div className="flex gap-4">
        <div className="space-y-2 flex-1">
          <Label>Visibility</Label>
          <Select value={visibility} onValueChange={setVisibility}>
            <SelectTrigger data-testid="note-visibility">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="private">
                <div className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Private
                </div>
              </SelectItem>
              <SelectItem value="shared">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Shared
                </div>
              </SelectItem>
              <SelectItem value="org">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Organization
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Options</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={pinned ? 'default' : 'outline'}
              size="icon"
              onClick={() => setPinned(!pinned)}
              title={pinned ? 'Unpin note' : 'Pin note'}
              data-testid="note-pin"
            >
              <Pin className={cn("h-4 w-4", pinned && "fill-current")} />
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  title="Set color"
                  style={color ? { backgroundColor: color, borderColor: color } : undefined}
                  data-testid="note-color"
                >
                  <Palette className={cn("h-4 w-4", color && "text-white")} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2">
                <div className="grid grid-cols-4 gap-2">
                  {COLORS.map((c) => (
                    <button
                      key={c.name}
                      className={cn(
                        "w-8 h-8 rounded-full border-2 transition-transform hover:scale-110",
                        color === c.value && "ring-2 ring-offset-2 ring-primary",
                        !c.value && "bg-background border-dashed"
                      )}
                      style={c.value ? { backgroundColor: c.value, borderColor: c.value } : undefined}
                      onClick={() => setColor(c.value)}
                      title={c.name}
                    />
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {/* Tags */}
      <div className="space-y-2">
        <Label>Tags</Label>
        <div className="flex gap-2 flex-wrap mb-2">
          {tags.map(tag => (
            <Badge key={tag} variant="secondary" className="gap-1">
              <Tag className="h-3 w-3" />
              {tag}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                className="ml-1 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Add a tag..."
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleTagKeyDown}
            className="flex-1"
            data-testid="note-tag-input"
          />
          <Button type="button" variant="outline" onClick={handleAddTag}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Linked Resource */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Link2 className="h-4 w-4" />
          Link to Resource (optional)
        </Label>
        <div className="flex gap-2">
          <Select
            value={linkedResourceType}
            onValueChange={(value) => {
              setLinkedResourceType(value);
              if (value === 'none') {
                setLinkedResourceId('');
                setLinkedResourceTitle('');
              }
            }}
          >
            <SelectTrigger className="w-[180px]" data-testid="note-resource-type">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {RESOURCE_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {linkedResourceType === 'project' ? (
            <Select
              value={linkedResourceId}
              onValueChange={handleProjectSelect}
              disabled={loadingProjects}
            >
              <SelectTrigger className="flex-1" data-testid="note-resource-id">
                <SelectValue placeholder={loadingProjects ? "Loading..." : "Select project"} />
              </SelectTrigger>
              <SelectContent>
                {projects.map(project => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : linkedResourceType && linkedResourceType !== 'none' ? (
            <div className="flex-1 flex gap-2">
              <Input
                placeholder="Resource ID"
                value={linkedResourceId}
                onChange={(e) => setLinkedResourceId(e.target.value)}
                className="flex-1"
                data-testid="note-resource-id"
              />
              <Input
                placeholder="Title (optional)"
                value={linkedResourceTitle}
                onChange={(e) => setLinkedResourceTitle(e.target.value)}
                className="flex-1"
                data-testid="note-resource-title"
              />
            </div>
          ) : null}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!content.trim() || saving}
          data-testid="note-save"
        >
          {saving ? 'Saving...' : note ? 'Update Note' : 'Create Note'}
        </Button>
      </div>
    </div>
  );
}
